import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { supabase } from '@flexwise/shared/lib/supabaseClient';

export interface UmfrageNoticeRpcPayload {
  semester_id: string;
  deadline: string | null;
  semester_name: string | null;
  has_submitted: boolean;
  deadline_passed: boolean;
}

export interface FlexModuleNoticePayload {
  period_id: string;
  period_status: string;
  settings: {
    team_input_enabled: boolean;
    allowed_roles: string[];
    allowed_staff_ids: string[];
    team_deadline: string | null;
    subtitle_short: string | null;
    subtitle_long: string | null;
  } | null;
}

export interface InfoBoardRpcPayload {
  bulletin_posts: any[];
  substitutions: any[];
  additional_assignments: any[];
  /** Active teacher-Umfrage CTA, null when none active/eligible.
   *  Returned by get_info_board_data_react v6+ (was previously a separate
   *  RPC fn_umfrage_active_notice). */
  umfrage_notice?: UmfrageNoticeRpcPayload | null;
  /** Active FlexModule registration period + minimal settings for the
   *  info-board notice card. Returned by get_info_board_data_react v7+
   *  (replaces the heavy useGetFlexModuleSettingsQuery call that hit
   *  fn_flex_module_page_init for just these 6 fields). */
  flex_module_notice?: FlexModuleNoticePayload | null;
}

export interface GetInfoBoardDataArgs {
  schoolId: string;
  profileId: string;
  teacherKurzel: string | null;
  date: string;
}

export type AdminPostFilter = 'all' | 'future' | 'past';

export interface AdminPostRow {
  id: string;
  title: string | null;
  content: string | null;
  priority: string | null;
  created_at: string;
  created_by: string | null;
  display_from: string | null;
  expires_at: string | null;
  is_public: boolean | null;
  visible_groups: string[] | null;
  visible_profiles: string[] | null;
}

export const infoBoardApi = createApi({
  reducerPath: 'infoBoardApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['InfoBoard', 'AdminPosts'],
  endpoints: (builder) => ({
    getInfoBoardData: builder.query<InfoBoardRpcPayload, GetInfoBoardDataArgs>({
      async queryFn(args) {
        const { schoolId, profileId, teacherKurzel, date } = args;

        const empty: InfoBoardRpcPayload = {
          bulletin_posts: [],
          substitutions: [],
          additional_assignments: [],
          umfrage_notice: null,
          flex_module_notice: null,
        };

        if (!schoolId || !profileId) {
          return { data: empty };
        }

        const fetchBulletinsDirect = async (daysAhead: number): Promise<InfoBoardRpcPayload> => {
          try {
            const isoNow = new Date().toISOString();
            const horizonMs = Math.max(0, Number(daysAhead) || 0) * 24 * 60 * 60 * 1000;
            const untilIso = new Date(Date.now() + horizonMs).toISOString();

            const { data: myGroups } = await supabase
              .from('user_group_members')
              .select('group_id')
              .eq('school_id', schoolId)
              .eq('user_id', profileId);
            const myGroupIds = Array.isArray(myGroups)
              ? myGroups.map((r: any) => r.group_id).filter(Boolean)
              : [];

            const { data: posts } = await supabase
              .from('bulletin_posts')
              .select(
                'id, title, content, priority, created_at, created_by, display_from, expires_at, is_public, visible_groups, visible_profiles, attachments',
              )
              .eq('school_id', schoolId)
              .lte('display_from', isoNow)
              .or(`expires_at.is.null,expires_at.gt.${isoNow}`)
              .order('created_at', { ascending: false })
              .limit(500);

            const visible = (posts || []).filter((p: any) => {
              const pub = !!p.is_public;
              const visProf = Array.isArray(p.visible_profiles) && p.visible_profiles.includes(profileId);
              const visGroup =
                Array.isArray(p.visible_groups) && myGroupIds.some((id: string) => p.visible_groups.includes(id));
              return pub || visProf || visGroup;
            });

            return { bulletin_posts: visible, substitutions: [], additional_assignments: [], umfrage_notice: null, flex_module_notice: null };
          } catch (e) {
            console.warn('infoBoardApi.fetchBulletinsDirect failed:', (e as any)?.message || e);
            return empty;
          }
        };

        const callRpc = async (daysAhead: number): Promise<InfoBoardRpcPayload> => {
          const timeoutMs = 10000;
          const timeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('timeout')), timeoutMs),
          );

          const rpcPromise = supabase.rpc('get_info_board_data_react' as any, {
            p_school_id: schoolId as any,
            p_profile_id: profileId as any,
            p_teacher_kurzel: teacherKurzel,
            p_date: date as any,
            p_days_ahead: daysAhead as any,
          } as any);

          const { data, error } = await Promise.race([rpcPromise, timeout]);
          if (error) {
            throw new Error((error as any).message || 'rpc_error');
          }
          return (data as InfoBoardRpcPayload) || empty;
        };

        try {
          // First try a 2-day window
          const data = await callRpc(2);
          return { data };
        } catch (e1: any) {
          try {
            const data = await callRpc(1);
            return { data };
          } catch (e2: any) {
            console.error('infoBoardApi.getInfoBoardData RPC failed, using fallback:', {
              first: e1?.message || String(e1),
              second: e2?.message || String(e2),
            });
            const fb = await fetchBulletinsDirect(2);
            return { data: fb };
          }
        }
      },
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, args) => [
        { type: 'InfoBoard' as const, id: `${args.schoolId}:${args.profileId}` },
      ],
    }),

    /**
     * Admin-only filtered post lists. Powers the "Nicht für mich /
     * Zukünftig / Vergangen" toggles. Each filter is a separate cache
     * entry keyed on `{schoolId, filter}` so toggling between them is
     * instant after first load (previously each toggle fired a fresh
     * REST call via useEffect).
     */
    getAdminPostsFiltered: builder.query<
      AdminPostRow[],
      { schoolId: string; filter: AdminPostFilter }
    >({
      async queryFn({ schoolId, filter }) {
        if (!schoolId) return { data: [] };
        const selectCols =
          'id, title, content, priority, created_at, created_by, display_from, expires_at, is_public, visible_groups, visible_profiles';
        let q = supabase.from('bulletin_posts').select(selectCols).eq('school_id', schoolId).limit(500);
        if (filter === 'all') {
          q = q.order('created_at', { ascending: false });
        } else if (filter === 'future') {
          const isoNow = new Date().toISOString();
          q = q.gt('display_from', isoNow).order('display_from', { ascending: true });
        } else if (filter === 'past') {
          const isoNow = new Date().toISOString();
          q = q.lt('expires_at', isoNow).order('expires_at', { ascending: false });
        }
        const { data, error } = await q;
        if (error) return { error: { message: error.message } as any };
        return { data: (data as AdminPostRow[] | null) ?? [] };
      },
      keepUnusedDataFor: 300,
      providesTags: (_r, _e, args) => [
        { type: 'AdminPosts' as const, id: `${args.schoolId}:${args.filter}` },
      ],
    }),
  }),
});

export interface UserNameRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

// Extend the slice with the author-name lookup. Capturing the return of
// injectEndpoints gives us properly typed hooks (including `refetch`,
// `isFetching`, and the `{ skip }` / `{ refetchOnMountOrArgChange }`
// options) without a manual type cast.
const infoBoardApiExt = infoBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserNamesByIds: builder.query<UserNameRow[], { ids: string[] }>({
      async queryFn({ ids }) {
        if (!ids.length) return { data: [] };
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name')
          .in('id', ids);
        if (error) return { error: { message: error.message } as any };
        return { data: (data as UserNameRow[] | null) ?? [] };
      },
      keepUnusedDataFor: 600,
    }),
  }),
});

export const {
  useGetInfoBoardDataQuery,
  useLazyGetInfoBoardDataQuery,
  useGetAdminPostsFilteredQuery,
  useGetUserNamesByIdsQuery,
} = infoBoardApiExt;
